/**
 * Custom Authentication Hook
 *
 * Re-exports the useAuthContext hook with a shorter name for convenience.
 * This follows the React hooks naming convention.
 *
 * @fileoverview Custom hook for accessing authentication context
 *
 * @example
 * import { useAuth } from '../hooks/useAuth';
 *
 * function MyComponent() {
 *   const { user, signIn, signOut, loading, error } = useAuth();
 *
 *   if (loading) return <Spinner />;
 *   if (!user) return <LoginForm />;
 *   return <Dashboard user={user} />;
 * }
 */

import { useAuthContext, AuthContextType } from '../contexts/AuthContext';

/**
 * Hook to access authentication state and methods
 *
 * Provides access to:
 * - user: Current authenticated user
 * - session: Current session data
 * - loading: Whether auth operation is in progress
 * - error: Error message from last operation
 * - signIn: Sign in with email/password
 * - signUp: Create new account
 * - signOut: Sign out current user
 * - resetPassword: Send password reset email
 * - updatePassword: Update password after reset
 * - clearError: Clear error message
 *
 * @returns Authentication context with user state and methods
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * // Check if user is logged in
 * const { user } = useAuth();
 * const isLoggedIn = !!user;
 *
 * @example
 * // Handle sign in
 * const { signIn, loading, error } = useAuth();
 *
 * const handleSubmit = async (email: string, password: string) => {
 *   const result = await signIn(email, password);
 *   if (result.success) {
 *     navigate('/home');
 *   }
 * };
 */
export const useAuth = (): AuthContextType => {
  return useAuthContext();
};

export default useAuth;
